import React, { useState, useRef } from 'react';
import { Box, Button, Paper, Typography, Alert, Dialog, DialogTitle, DialogContent } from '@mui/material';
import RichTextContent from '../../common/RichTextContent';

interface HotspotPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const HotspotPlayer: React.FC<HotspotPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    let { instruction, imageUrl, hotspots = [] } = content;
    const learningContent = content.learningContent || '';
    const imageRef = useRef<HTMLImageElement>(null);

    const [clickedHotspots, setClickedHotspots] = useState<Set<string>>(new Set());
    const [feedbackDialog, setFeedbackDialog] = useState<any>(null);
    const [imageBlobUrl, setImageBlobUrl] = useState<string>('');

    const isMediaLibrary = imageUrl && imageUrl.startsWith('/api/media/');

    // Load image from media library with authentication
    React.useEffect(() => {
        if (isMediaLibrary && imageUrl) {
            const fetchImage = async () => {
                try {
                    const uuid = imageUrl.split('/').pop();
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${uuid}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        setImageBlobUrl(url);
                    } else {
                        console.error('Failed to load image:', response.status);
                    }
                } catch (error) {
                    console.error('Error loading image:', error);
                }
            };
            fetchImage();
        }

        // Cleanup blob URL on unmount
        return () => {
            if (imageBlobUrl) {
                URL.revokeObjectURL(imageBlobUrl);
            }
        };
    }, [imageUrl, isMediaLibrary]);

    // Use blob URL if from media library, otherwise use direct URL
    const displayImageUrl = isMediaLibrary ? imageBlobUrl : imageUrl;

    const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // Find clicked hotspot
        const clicked = hotspots.find((hs: any) => {
            const distance = Math.sqrt(Math.pow(x - hs.x, 2) + Math.pow(y - hs.y, 2));
            return distance <= hs.radius;
        });

        if (clicked && !clickedHotspots.has(clicked.id)) {
            setClickedHotspots(new Set(clickedHotspots).add(clicked.id));
            setFeedbackDialog(clicked);
        }
    };

    const score = hotspots.length > 0 ? Math.round((clickedHotspots.size / hotspots.length) * 100) : 0;
    const passed = score >= (lesson.minScore || 70);
    const allFound = clickedHotspots.size === hotspots.length;

    // Issue #332: Score ans Backend melden, bevor die Lektion abgeschlossen wird.
    const handleComplete = async () => {
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    return (
        <Box>
            {learningContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <RichTextContent html={learningContent} />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {instruction || 'Klicken Sie auf die markierten Bereiche im Bild'}
                </Typography>

                <Alert severity={passed ? 'success' : 'info'} sx={{ my: 2 }}>
                    {clickedHotspots.size} von {hotspots.length} Hotspots gefunden ({score}%)
                </Alert>

                {displayImageUrl && (
                    <Box
                        sx={{
                            position: 'relative',
                            maxWidth: 800,
                            margin: '0 auto',
                            cursor: 'pointer'
                        }}
                        onClick={handleImageClick}
                    >
                        <img
                            ref={imageRef}
                            src={displayImageUrl}
                            alt="Hotspot"
                            style={{ width: '100%', display: 'block' }}
                        />

                        {/* Show found hotspots */}
                        {hotspots.map((hs: any) => (
                            clickedHotspots.has(hs.id) && (
                                <Box
                                    key={hs.id}
                                    sx={{
                                        position: 'absolute',
                                        left: `${hs.x}%`,
                                        top: `${hs.y}%`,
                                        transform: 'translate(-50%, -50%)',
                                        width: `${hs.radius * 2}%`,
                                        height: `${hs.radius * 2}%`,
                                        borderRadius: '50%',
                                        border: '3px solid green',
                                        bgcolor: 'rgba(0, 255, 0, 0.2)',
                                        pointerEvents: 'none'
                                    }}
                                />
                            )
                        ))}
                    </Box>
                )}

                {allFound && (
                    <Alert severity="success" sx={{ mt: 3 }}>
                        Alle Hotspots gefunden! Gut gemacht!
                    </Alert>
                )}

                {!allFound && passed && (
                    <Alert severity="success" sx={{ mt: 3 }}>
                        Sie haben {score}% erreicht und damit bestanden! Sie können fortfahren oder weitere Hotspots suchen.
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" onClick={handleComplete} disabled={!passed}>
                    Bestätigen
                </Button>
            </Box>

            {/* Feedback Dialog */}
            <Dialog open={!!feedbackDialog} onClose={() => setFeedbackDialog(null)}>
                <DialogTitle>{feedbackDialog?.label}</DialogTitle>
                <DialogContent>
                    <Typography>{feedbackDialog?.feedback || 'Hotspot gefunden!'}</Typography>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default HotspotPlayer;
